import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useKernelStore } from '@/kernel/kernelStores';
import type { UserAction } from '#types/types';
import type { TimelineActionSyncCategory } from '#lib/leasing/timeline-events';

const SYNC_INTERVAL_MS = 2000;
// Chaque échec consécutif recule la prochaine tentative d'une seconde de plus. Sans ça, une
// coupure PostgreSQL de 15 min a produit 3104 rejeux du même lot pour un seul utilisateur
// (signal b242000c096e du 2026-07-30).
const SYNC_RETRY_STEP_MS = 1000;
const MAX_SYNC_RETRY_DELAY_MS = 90_000;
const MAX_SYNC_ACTIONS_PER_BATCH = 200;
// Un lot refusé repart en QUEUE et non en tête, sinon une seule action que le serveur refusera
// toujours (400 zod, timestamp invalide) bloque toute la télémétrie derrière elle. Faire tourner
// la file est sans danger : chaque action porte son `timestamp` et son `id`, le serveur écrit
// `createdAt` depuis l'action et déduplique par `id`, donc l'ordre d'envoi n'a aucun effet.
// Au bout de MAX_SYNC_STRIKES_PER_ACTION refus, l'action est abandonnée : la rotation seule ne
// termine pas quand c'est la REQUÊTE qui est refusée et non le lot (404 session introuvable,
// 401), cas où tous les lots échouent quel que soit leur contenu.
const MAX_SYNC_STRIKES_PER_ACTION = 40;
const MAX_TRACKED_ACTION_IDS = 20_000;
const MAX_PENDING_QUEUE_SIZE = 2_000;
const HIGH_FREQUENCY_TELEMETRY_TTL_HINT_MS = 60 * 60 * 1000; // 1h
const DEFAULT_TELEMETRY_TTL_HINT_MS = 24 * 60 * 60 * 1000; // 24h
const AGENTIC_ACTIONS = new Set<string>([
  'Generation Initiated',
  'Task Result Selected',
]);
const HIGH_FREQUENCY_TELEMETRY_ACTIONS = new Set<string>([
  'Workspace Frame Moved',
  'Workspace Frame Resized',
]);

type ActionSyncCategoryAndTtlHint = {
  category: TimelineActionSyncCategory;
  ttlMsHint?: number;
};

type SyncActionPayload = {
  id: string;
  timestamp: string;
  name: string;
  payload: Record<string, unknown>;
  category: TimelineActionSyncCategory;
  ttlMsHint?: number;
};

// `strikes` ne part jamais sur le réseau : il compte les refus subis par cette action.
type QueuedSyncAction = SyncActionPayload & { strikes: number };

const toWireAction = ({ strikes: _strikes, ...action }: QueuedSyncAction): SyncActionPayload => action;

type ActionHistorySyncScope = {
  projectId: string | null;
  chatSessionId?: string | null;
};

const getCategoryAndTtlHint = (actionName: string): ActionSyncCategoryAndTtlHint => {
  if (AGENTIC_ACTIONS.has(actionName)) {
    return { category: 'AGENTIC' };
  }

  const ttlMsHint = HIGH_FREQUENCY_TELEMETRY_ACTIONS.has(actionName)
    ? HIGH_FREQUENCY_TELEMETRY_TTL_HINT_MS
    : DEFAULT_TELEMETRY_TTL_HINT_MS;
  return { category: 'TELEMETRY', ttlMsHint };
};

const isValidAction = (action: UserAction | null | undefined): action is UserAction =>
  Boolean(
    action &&
      typeof action.id === 'string' &&
      action.id.trim().length > 0 &&
      typeof action.name === 'string' &&
      action.name.trim().length > 0 &&
      typeof action.timestamp === 'string' &&
      action.timestamp.trim().length > 0
  );

export const useActionHistorySync = ({ projectId, chatSessionId = null }: ActionHistorySyncScope) => {
  const actionHistory = useKernelStore((state) => state.actionHistory);
  const pendingQueueRef = useRef<QueuedSyncAction[]>([]);
  const seenActionIdsRef = useRef<Set<string>>(new Set());
  const projectIdRef = useRef<string | null>(null);
  const chatSessionIdRef = useRef<string | null>(null);
  const isSyncingRef = useRef(false);
  const retryDelayMsRef = useRef(0);
  const nextAttemptAtRef = useRef(0);

  useEffect(() => {
    if (projectIdRef.current !== projectId) {
      projectIdRef.current = projectId;
      pendingQueueRef.current = [];
      seenActionIdsRef.current = new Set();
      retryDelayMsRef.current = 0;
      nextAttemptAtRef.current = 0;
    }
  }, [projectId]);

  useEffect(() => {
    chatSessionIdRef.current = chatSessionId;
  }, [chatSessionId]);

  useEffect(() => {
    if (!projectId) return;
    if (actionHistory.length === 0) return;

    const latestAction = actionHistory[actionHistory.length - 1];
    if (!isValidAction(latestAction)) return;

    const seenActionIds = seenActionIdsRef.current;
    if (seenActionIds.has(latestAction.id)) return;

    if (seenActionIds.size >= MAX_TRACKED_ACTION_IDS) {
      seenActionIds.clear();
    }
    seenActionIds.add(latestAction.id);

    const { category, ttlMsHint } = getCategoryAndTtlHint(latestAction.name);
    pendingQueueRef.current.push({
      id: latestAction.id,
      timestamp: latestAction.timestamp,
      name: latestAction.name,
      payload: latestAction.payload ?? {},
      category,
      strikes: 0,
      ...(typeof ttlMsHint === 'number' ? { ttlMsHint } : {}),
    });
    if (pendingQueueRef.current.length > MAX_PENDING_QUEUE_SIZE) {
      const overflow = pendingQueueRef.current.length - MAX_PENDING_QUEUE_SIZE;
      pendingQueueRef.current.splice(0, overflow);
    }
  }, [actionHistory, projectId]);

  useEffect(() => {
    if (!projectId) return;

    const flush = async () => {
      if (isSyncingRef.current) return;
      if (Date.now() < nextAttemptAtRef.current) return;

      const trimmedProjectId = projectIdRef.current?.trim() ?? '';
      if (!trimmedProjectId) return;

      const pendingQueue = pendingQueueRef.current;
      if (pendingQueue.length === 0) return;
      const batch = pendingQueue.slice(0, MAX_SYNC_ACTIONS_PER_BATCH);
      // Le lot est retrouvé par `id` et non par indice : l'effet d'empilement peut purger la
      // file par l'avant (MAX_PENDING_QUEUE_SIZE) pendant que la requête est en vol, auquel cas
      // `splice(0, n)` retirerait les mauvaises actions.
      const batchIds = new Set(batch.map((action) => action.id));

      isSyncingRef.current = true;
      try {
        await axios.post(
          '/api/events/sync-actions',
          {
            targetEntityType: 'Project',
            targetEntityId: trimmedProjectId,
            ...(chatSessionIdRef.current?.trim()
              ? { chatSessionId: chatSessionIdRef.current.trim() }
              : {}),
            actions: batch.map(toWireAction),
          },
          { withCredentials: true }
        );
        pendingQueueRef.current = pendingQueueRef.current.filter((action) => !batchIds.has(action.id));
        retryDelayMsRef.current = 0;
        nextAttemptAtRef.current = 0;
      } catch (error) {
        const restants: QueuedSyncAction[] = [];
        const refuses: QueuedSyncAction[] = [];
        let abandonnees = 0;
        for (const action of pendingQueueRef.current) {
          if (!batchIds.has(action.id)) {
            restants.push(action);
            continue;
          }
          action.strikes += 1;
          if (action.strikes >= MAX_SYNC_STRIKES_PER_ACTION) abandonnees += 1;
          else refuses.push(action);
        }
        // Les refusés repassent DERRIÈRE ceux qui n'ont pas encore été tentés.
        pendingQueueRef.current = [...restants, ...refuses];
        retryDelayMsRef.current = Math.min(
          retryDelayMsRef.current + SYNC_RETRY_STEP_MS,
          MAX_SYNC_RETRY_DELAY_MS
        );
        nextAttemptAtRef.current = Date.now() + retryDelayMsRef.current;
        if (abandonnees > 0) {
          console.warn(
            `[ActionSync] ${abandonnees} action(s) abandonnée(s) après ${MAX_SYNC_STRIKES_PER_ACTION} refus`
          );
        }
        console.error('[ActionSync] Failed to sync actions', error);
      } finally {
        isSyncingRef.current = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void flush();
    }, SYNC_INTERVAL_MS);

    void flush();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [projectId]);
};
